import { useCallback, useEffect, useRef, useState } from "react";
import { PlateProvider } from "@udecode/plate-core";

import { isAndroid, isMobileNative } from "~/utils/helpers";
import { getNoteColorConfig, toPx } from "~/utils/components";
import { useBreakpoints } from "~/hooks/useBreakpoints";

import { SheetModal } from "../sheetModal/SheetModal";
import { NoteEditorModalHeader } from "./NoteEditorModalHeader";
import { NoteEditorModalBody } from "./NoteEditorModalBody";
import { DEFAULT_NOTE_COLOR } from "../../constants";
import { DialogModal } from "../dialogModal/DialogModal";

import "./NoteEditorModal.css";

import type { CSSProperties } from "react";
import type { TextEditorSaveEvent } from "../textEditor/TextEditor";
import type { NoteColor } from "../noteCard/NoteCard";
import type { NoteEditorModalBodyRef } from "./NoteEditorModalBody";

export interface NoteEditorData {
  color: NoteColor;
  value: string;
}

export interface NoteEditorModalChangeEvent extends NoteEditorData {}

export interface NoteEditorModalSaveEvent extends NoteEditorModalChangeEvent {}

export interface NoteEditorModalProps {
  color?: NoteColor;
  id: string;
  initialValue?: string;
  isOpen?: boolean;
  limit?: number;
  resetAfterSave?: boolean;
  timestamp?: number;
  title?: string;
  onCancel?: () => void;
  onChange?: (event: NoteEditorModalChangeEvent) => void;
  onRequestClose?: () => void;
  onSave?: (event: NoteEditorModalSaveEvent) => Promise<void>;
  onDraftSave?: (event: NoteEditorModalSaveEvent) => Promise<void>;
}

export function NoteEditorModal({
  color = DEFAULT_NOTE_COLOR,
  id,
  initialValue,
  isOpen,
  limit,
  timestamp,
  title,
  resetAfterSave,
  onCancel,
  onChange,
  onRequestClose,
  onSave,
  onDraftSave,
}: NoteEditorModalProps) {
  const ref = useRef<NoteEditorModalBodyRef>(null);

  const breakpoints = useBreakpoints();

  // Persist note + color state here so that it can be
  // dispatched as a whole
  const [noteColor, setNoteColor] = useState(color);
  const [prevNoteColor, setPrevNoteColor] = useState<NoteColor>();
  const [noteValue, setNoteValue] = useState(initialValue ?? "");

  // React to props.color change
  if (color !== prevNoteColor) {
    setNoteColor(color);
    setPrevNoteColor(color);
  }

  // The size of the virtual keyboard
  const [insetBottom, setInsetBottom] = useState<number>(0);

  const colorConfig = getNoteColorConfig(noteColor);

  const dialogModalCustomProperties = {
    "--dialog-modal-body-padding": "var(--size-1-5)",
    "--dialog-modal-panel-background-color": colorConfig.background,
  } as CSSProperties;

  const sheetModalCustomProperties = {
    "--sheet-modal-z-index": "82",
    "--sheet-modal-inset-bottom": insetBottom ? toPx(insetBottom) : undefined,
  } as CSSProperties;

  const handleColorChange = useCallback(
    (color: NoteColor) => {
      setNoteColor(color);
      onDraftSave?.({ value: noteValue, color });
    },
    [noteValue, onDraftSave],
  );

  const handleDraftSave = useCallback(
    async (event: TextEditorSaveEvent) => {
      setNoteValue(event.value);
      return onDraftSave?.({ value: event.value, color: noteColor });
    },
    [noteColor, onDraftSave],
  );

  const handleCancel = useCallback(() => {
    onCancel?.();
    onRequestClose?.();
  }, [onCancel, onRequestClose]);

  const renderHeader = useCallback(() => {
    return (
      <NoteEditorModalHeader
        color={noteColor}
        title={title}
        timestamp={timestamp}
        onCancel={handleCancel}
        onColorChange={handleColorChange}
      />
    );
  }, [handleCancel, handleColorChange, noteColor, timestamp, title]);

  const renderEditor = useCallback(() => {
    return (
      <PlateProvider id={id}>
        <NoteEditorModalBody
          useSimpleEditor={isAndroid()}
          autoSave={Boolean(onDraftSave)}
          color={noteColor}
          id={id}
          initialValue={initialValue}
          limit={limit}
          ref={ref}
          resetAfterSave={resetAfterSave}
          onSave={onSave}
          onDraftSave={handleDraftSave}
        />
      </PlateProvider>
    );
  }, [
    handleDraftSave,
    id,
    initialValue,
    limit,
    noteColor,
    onDraftSave,
    onSave,
    resetAfterSave,
  ]);

  // Mobile Navtive event listeners
  useEffect(() => {
    if (isMobileNative()) {
      const handleKeyboard = (e: CustomEventInit) => {
        setInsetBottom(e.detail.height);
      };

      document.addEventListener("classroom:keyboardWillShow", handleKeyboard);
      document.addEventListener("classroom:keyboardWillHide", handleKeyboard);

      return () => {
        document.removeEventListener(
          "classroom:keyboardWillShow",
          handleKeyboard,
        );
        document.removeEventListener(
          "classroom:keyboardWillHide",
          handleKeyboard,
        );
      };
    }
  }, []);

  return breakpoints.lg ? (
    <DialogModal
      isOpen={isOpen}
      style={dialogModalCustomProperties}
      header={renderHeader()}
      onRequestClose={onRequestClose}
    >
      <DialogModal.Body>{renderEditor()}</DialogModal.Body>
    </DialogModal>
  ) : (
    <SheetModal
      disableGrabber
      disableGestures
      isOpen={isOpen}
      size="lg"
      style={sheetModalCustomProperties}
      header={renderHeader()}
      onRequestClose={onRequestClose}
    >
      {renderEditor()}
    </SheetModal>
  );
}
